/*
  HLS in-stock enforcement for collection + search filters
  --------------------------------------------------------
  Nir 2026-07-30 (v4).

  GOAL: the number in brackets next to a size must be the number of products you
  can actually BUY in that size, and clicking it must return exactly those
  products. Before this, "Extra large (XL) (20)" counted every product that owns
  an XL variant — sold out or not — so clicking it produced items you could not
  buy in XL.

  HOW: pin `filter.v.availability=1` onto every listing request. Shopify scopes
  variant filters to the SAME variant, so "size XL" + "available" means "the XL
  variant is in stock". Shopify then computes the bracket counts from that same
  filtered set, which is what makes the counts and the results agree. We
  deliberately do NOT compute counts ourselves: the size facet is a taxonomy
  filter whose values are GIDs (gid://shopify/TaxonomyValue/17090) and whose
  labels do not match the variant options ("Double extra large (XXL)" is the
  variant option "2XL"), so any string matching here is guaranteed to rot.

  SIDE EFFECT: a product with zero sellable variants drops off the listing.
  Today that is exactly one product, "Standard Logo Beanie (Black)". To bring
  fully sold-out products back, delete the applyOnLanding() call below — the
  counts then go back to being inflated.

  The Availability facet stays hidden in facets.liquid (filter list + all four
  pill regions), so none of this is exposed as a checkbox the shopper manages.

  LOAD ORDER — loaded in <head> WITHOUT defer, on purpose.
  v2 shipped it deferred and silently broken: a deferred <head> script runs when
  document.readyState is ALREADY "interactive" (NOT "loading"), so a
  `readyState === 'loading'` guard falls straight through and runs before
  facets.js (deferred, in the body) has defined FacetFiltersForm. The patch
  no-op'd on first load and only appeared to work after a refresh, because the
  landing path does not need that class. Do not re-add `defer`.
*/
(function () {
  'use strict';

  var PARAM = 'filter.v.availability';
  var VALUE = '1';

  function toParams(search) {
    return new URLSearchParams(search.indexOf('?') === 0 ? search.slice(1) : search);
  }

  // Listing pages are always in-stock-only. Other params (q, sort_by, price,
  // size, page) are preserved untouched.
  function normalize(search) {
    var params = toParams(search);
    params.set(PARAM, VALUE);
    return params.toString();
  }

  function needsFix(search) {
    return toParams(search).get(PARAM) !== VALUE;
  }

  // A "real" filter means the server already rendered a product set that can
  // contain unbuyable matches — that has to be corrected by a reload.
  // The VALUE has to be non-empty: facets.js submits the price-range inputs
  // even when they are blank, so `?filter.v.price.gte=&filter.v.price.lte=` is
  // exactly what lands in the URL after the shopper unticks their last size.
  // Treating that as a real filter would cost a full reload for nothing.
  function hasShopperFilter(search) {
    var found = false;
    toParams(search).forEach(function (value, key) {
      if (value !== '' && key.indexOf('filter.') === 0 && key !== PARAM) found = true;
    });
    return found;
  }

  function isListingPage() {
    return Boolean(document.getElementById('product-grid') && document.querySelector('facet-filters-form'));
  }

  /* ---- 1. Landing on a filtered URL (shared link, bookmark, old index) ----
     Runs at parse time, before <body> paints, so no flash of unbuyable
     results. Only for URLs that already carry a shopper filter: those are the
     misleading ones. Gated on the query string because there is no DOM yet. */
  function enforceOnLanding() {
    var search = window.location.search;
    if (!needsFix(search) || !hasShopperFilter(search)) return false;

    // replace() not assign() — a correction, not a history entry. Stable: the
    // corrected URL normalizes to itself, so it cannot loop.
    window.location.replace(window.location.pathname + '?' + normalize(search));
    return true;
  }

  /* ---- 2. Unfiltered landing -------------------------------------------
     No reload here: the only thing wrong on a bare listing is the bracket
     counts, and those live inside a closed dropdown. A section fetch fixes the
     counts and the grid together, without costing a full page load. History is
     left alone (updateURLHash=false) so the back button and the clean URL both
     survive. */
  function applyOnLanding() {
    if (!isListingPage() || !needsFix(window.location.search)) return;
    FacetFiltersForm.renderPage(normalize(window.location.search), null, false);
  }

  /* ---- 3. Every AJAX filter update --------------------------------------
     renderPage is the single funnel for desktop facets, the mobile drawer,
     pill removal and popstate, and it derives the fetch URL, the cache key and
     the pushState URL from one string — so patching it keeps all three in
     agreement. */
  function patchRenderPage() {
    if (typeof FacetFiltersForm === 'undefined' || !FacetFiltersForm.renderPage) return false;
    if (FacetFiltersForm.renderPage.hlsInstockPatched) return true;

    var original = FacetFiltersForm.renderPage.bind(FacetFiltersForm);
    var patched = function (searchParams, event, updateURLHash) {
      // Passing undefined through preserves the original default (true).
      return original(normalize(searchParams), event, updateURLHash);
    };
    patched.hlsInstockPatched = true;
    FacetFiltersForm.renderPage = patched;
    return true;
  }

  /* Wait for facets.js however it ends up being loaded. Deferred scripts all
     run before DOMContentLoaded, so that listener is the reliable signal; the
     poll is a backstop in case it is ever switched to async. */
  function whenReady(done) {
    if (patchRenderPage()) return done();

    var settled = false;
    function attempt() {
      if (settled) return true;
      if (!patchRenderPage()) return false;
      settled = true;
      clearInterval(timer);
      done();
      return true;
    }

    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (attempt() || attempts > 100) clearInterval(timer);
    }, 50);

    document.addEventListener('DOMContentLoaded', attempt);
    window.addEventListener('load', attempt);
  }

  // If we are navigating away, do not bother wiring anything up.
  if (enforceOnLanding()) return;

  whenReady(applyOnLanding);
})();
